import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomButton } from '@/components/molecules/CustomButton';
import { useToast } from '@/hooks/useToastContext';
import { confirmEvolution, refuseEvolution, type EvolutionData } from '@/services/pokemon';

interface EvolutionModalProps {
  evolution: EvolutionData;
  onDone: () => void;
}

export const EvolutionModal = ({ evolution, onDone }: EvolutionModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: confirm, isPending: isConfirming } = useMutation({
    mutationFn: () => confirmEvolution(evolution.allyOwnedPokemonId, evolution.evolvedPokemonId),
    onSuccess: (data) => {
      const pokedexPart = data.isNewInPokedex ? ` 📖 +${data.pokeDollarsEarned.toLocaleString('fr-FR')} PokéDollars (nouveau Pokédex !)` : '';
      showToast('success', `${evolution.fromName} a évolué en ${evolution.evolvedPokemonName} !${pokedexPart}`);
      queryClient.invalidateQueries({ queryKey: ['pokemons'] });
      queryClient.invalidateQueries({ queryKey: ['myPension'] });
      onDone();
    },
    onError: (err: Error) => {
      showToast('error', err.message);
      onDone();
    },
  });

  const { mutate: refuse, isPending: isRefusing } = useMutation({
    mutationFn: () => refuseEvolution(evolution.allyOwnedPokemonId),
    onSuccess: () => onDone(),
    onError: () => onDone(),
  });

  const isLoading = isConfirming || isRefusing;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-surface shadow-2xl">
        <div className="bg-primary-500 px-6 py-5 text-center">
          <p className="font-lexend text-xl font-bold text-white">
            ✨ {evolution.fromName} peut évoluer !
          </p>
        </div>
        <div className="space-y-4 p-5">
          <p className="text-content/70 text-sm text-center">
            {evolution.fromName} peut évoluer en{' '}
            <strong className="text-content">{evolution.evolvedPokemonName}</strong> !
            Confirmes-tu l'évolution ?
          </p>
          <div className="flex gap-3">
            <CustomButton
              title="Refuser"
              color="dark"
              variant="ghost"
              fullWidth
              rounded="full"
              loading={isRefusing}
              disabled={isLoading}
              onClick={() => refuse()}
            />
            <CustomButton
              title="Confirmer"
              color="primary"
              fullWidth
              rounded="full"
              loading={isConfirming}
              disabled={isLoading}
              onClick={() => confirm()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
